import { Paperclip } from 'lucide-react';
import type { MouseEvent } from 'react';
import { attachmentLabel } from '../../../lib/contact-rows';
import type { WebOutreachAttachment } from '../../../lib/contact-book';
import { docViewerKind } from '../../../lib/doc-viewer';
import { openDocViewer } from '../../../lib/doc-viewer-presenter';
import styles from './contacts.module.css';

/**
 * The files sent with one message.
 *
 * A chip rather than an inline preview: a thread is a transcript, and an inline render of an attached PDF
 * would make the conversation unreadable, which is the same reason the thread got its own screen.
 *
 * A plain click opens the in-app viewer, the same one a chat attachment opens. The raw Wix media URL is
 * served with no charset, so a browser tab reads a Hebrew `.md` as Latin-1 and shows mojibake; the viewer
 * decodes UTF-8 and renders the markdown. The chip stays a real link, so cmd/ctrl/shift/alt-click and
 * middle-click still open the raw file in a tab.
 *
 * Its own module so every thread surface (the shared `OutreachThreadTranscript`) renders attachments the
 * exact same way — one implementation, so no two threads can drift on how a file appears.
 */
export function MessageAttachments({ attachments }: { attachments?: WebOutreachAttachment[] }) {
  if (!attachments?.length) return null;
  return (
    <div className={styles.attachments}>
      {attachments.map((attachment, index) => (
        <a
          key={`${attachment.url ?? ''}-${index}`}
          className={styles.attachment}
          href={attachment.url || undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => openInViewer(e, attachment)}
        >
          <Paperclip size={16} aria-hidden />
          <span className={styles.attachmentName}>{attachmentLabel(attachment)}</span>
        </a>
      ))}
    </div>
  );
}

function openInViewer(e: MouseEvent<HTMLAnchorElement>, attachment: WebOutreachAttachment) {
  if (!attachment.url || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  const kind = docViewerKind(attachment.fileName, attachment.url, attachment.mimeType);
  openDocViewer({ file: { name: attachment.fileName ?? '', isApp: false, content: attachment.url }, kind, pageName: 'document_viewer' });
}
